import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import QRCode from 'react-qr-code';

import { PageHeader } from '../../components/PageHeader';
import WithLoader from '../../components/_hocs/withLoader/WithPaginationLoader';
import { PasswordInput } from '../../components/Inputs/PasswordInput';
import { userProfile } from '../../actions/auth';
import Strings from '../../Strings';
import {
  actions,
  GET_2FA_STATUS_RESULT,
  DISABLE_2FA_RESULT,
  REGENERATE_2FA_CODES_RESULT,
  ENABLE_2FA_START_RESULT,
  ENABLE_2FA_CONFIRM_RESULT,
  UPDATE_PASSWORD_RESULT,
} from './redux/actions';
import './TwoFactorSettings.scss';

// Cast Strings to any to avoid TypeScript issues with dynamically added properties
const S = Strings as any;

interface TwoFactorStatus {
  enabled: boolean;
  backup_codes_count?: number;
  method?: string;
}

interface Enable2FAData {
  secret: string;
  qr_url: string;
  method: string;
}

type TwoFactorMethod = 'totp' | 'sms' | 'email';

interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

const checkPasswordRequirements = (password: string): PasswordRequirements => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[!@#$%^&*()_+\-=[\]{}|;':",./<>?]/.test(password),
});

const isPasswordValid = (reqs: PasswordRequirements): boolean =>
  reqs.minLength && reqs.hasUppercase && reqs.hasLowercase && reqs.hasNumber && reqs.hasSpecial;

interface TwoFactorSettingsProps {
  get2FAStatus: () => Promise<{ type: string; response: TwoFactorStatus }>;
  disable2FA: (password: string) => Promise<{ type: string; response?: any }>;
  regenerate2FACodes: (password: string) => Promise<{ type: string; response?: { backup_codes: string[] } }>;
  enable2FAStart: (method: TwoFactorMethod) => Promise<{ type: string; response: Enable2FAData }>;
  enable2FAConfirm: (code: string) => Promise<{ type: string; response?: { backup_codes: string[] } }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ type: string; response?: any }>;
  refreshProfile: () => Promise<{ type: string; response?: any }>;
}

function TwoFactorSettings(props: TwoFactorSettingsProps) {
  const { get2FAStatus, disable2FA, regenerate2FACodes, enable2FAStart, enable2FAConfirm, updatePassword, refreshProfile } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Enable flow state
  const [enableStep, setEnableStep] = useState<'idle' | 'select-method' | 'setup' | 'verify' | 'complete'>('idle');
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod>('totp');
  const [enableData, setEnableData] = useState<Enable2FAData | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Disable/regenerate state
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await get2FAStatus();
      if (response.type === GET_2FA_STATUS_RESULT) {
        setStatus(response.response);
      } else {
        setError(S.twoFactorSettings?.loadError || 'Failed to load 2FA status');
      }
    } catch {
      setError(S.twoFactorSettings?.loadError || 'Failed to load 2FA status');
    }
    setIsLoading(false);
  };

  const handleDisable = async () => {
    if (!password) {
      setError(S.twoFactorSettings?.passwordRequired || 'Password is required');
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      const response = await disable2FA(password);
      if (response.type === DISABLE_2FA_RESULT) {
        setStatus({ enabled: false });
        setShowDisableConfirm(false);
        setPassword('');
      } else {
        setError(S.twoFactorSettings?.disableError || 'Failed to disable 2FA');
      }
    } catch {
      setError(S.twoFactorSettings?.disableError || 'Failed to disable 2FA');
    }
    setActionLoading(false);
  };

  const handleRegenerate = async () => {
    if (!password) {
      setError(S.twoFactorSettings?.passwordRequired || 'Password is required');
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      const response = await regenerate2FACodes(password);
      if (response.type === REGENERATE_2FA_CODES_RESULT && response.response) {
        setBackupCodes(response.response.backup_codes);
        setStatus(prev => prev ? { ...prev, backup_codes_count: response.response!.backup_codes.length } : prev);
        setShowRegenerateConfirm(false);
        setPassword('');
      } else {
        setError(S.twoFactorSettings?.regenerateError || 'Failed to regenerate backup codes');
      }
    } catch {
      setError(S.twoFactorSettings?.regenerateError || 'Failed to regenerate backup codes');
    }
    setActionLoading(false);
  };

  const handleEnableStart = () => {
    setEnableStep('select-method');
    setError(null);
  };

  const handleMethodSelect = async (method: TwoFactorMethod) => {
    setSelectedMethod(method);
    setActionLoading(true);
    setError(null);
    try {
      const response = await enable2FAStart(method);
      if (response.type === ENABLE_2FA_START_RESULT) {
        setEnableData(response.response);
        setEnableStep('setup');
      } else {
        setError(S.twoFactorSettings?.enableError || 'Failed to start 2FA setup');
      }
    } catch {
      setError(S.twoFactorSettings?.enableError || 'Failed to start 2FA setup');
    }
    setActionLoading(false);
  };

  const handleEnableConfirm = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError(S.errors?.enter6DigitCode || 'Please enter the 6-digit code');
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      const response = await enable2FAConfirm(verifyCode);
      if (response.type === ENABLE_2FA_CONFIRM_RESULT && response.response) {
        setBackupCodes(response.response.backup_codes);
        setEnableStep('complete');
        setStatus({ enabled: true, backup_codes_count: response.response.backup_codes.length });
        refreshProfile();
      } else {
        setError(S.twoFactor?.invalidCode || 'Invalid code. Please try again.');
      }
    } catch {
      setError(S.twoFactor?.invalidCode || 'Invalid code. Please try again.');
    }
    setActionLoading(false);
  };

  const handleCancelEnable = () => {
    setEnableStep('idle');
    setSelectedMethod('totp');
    setEnableData(null);
    setVerifyCode('');
    setError(null);
  };

  const handleBackupCodesDismiss = async () => {
    setBackupCodes(null);
    if (enableStep === 'complete') {
      // The refreshed profile (user_two_fa_enabled) lifts the org-level 2FA restriction of the
      // sidebar through Redux. No page reload: it would cancel whatever the user does next.
      try {
        await refreshProfile();
      } catch {
        // Keep the current view; the next profile load picks the change up.
      }
      setEnableStep('idle');
      setEnableData(null);
      setVerifyCode('');
    }
  };

  const handlePasswordChange = async () => {
    if (!isPasswordValid(checkPasswordRequirements(newPassword)) || newPassword !== confirmPassword || newPassword === currentPassword) {
      return;
    }

    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError(S.twoFactorSettings?.currentPasswordRequired || 'Current password is required');
      return;
    }

    if (!newPassword) {
      setPasswordError(S.twoFactorSettings?.newPasswordRequired || 'New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(S.twoFactorSettings?.passwordTooShort || 'Password must be at least 8 characters');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError(S.twoFactorSettings?.passwordMustBeDifferent || 'New password must be different from current password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(S.twoFactorSettings?.passwordsDoNotMatch || 'Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await updatePassword(currentPassword, newPassword);
      if (response.type === UPDATE_PASSWORD_RESULT) {
        setPasswordSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordSuccess(false);
        }, 1500);
      } else {
        // Extract error message from backend response if available
        // Use standard_error.message or error_msg which don't include the UUID
        const errorMessage = response.response?.data?.standard_error?.message
          || response.response?.data?.error_msg
          || S.twoFactorSettings?.passwordUpdateError
          || 'Failed to update password';
        setPasswordError(errorMessage);
      }
    } catch {
      setPasswordError(S.twoFactorSettings?.passwordUpdateError || 'Failed to update password');
    }
    setPasswordLoading(false);
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  const renderBackupCodes = () => {
    if (!backupCodes) return null;

    return (
      <div className="two-factor-settings__backup-codes-modal">
        <div className="two-factor-settings__backup-codes-content">
          <h3>{S.twoFactorSettings?.backupCodesTitle || 'Your Backup Codes'}</h3>
          <p className="two-factor-settings__backup-codes-warning">
            {S.twoFactorSettings?.backupCodesWarning ||
              'Save these codes in a secure location. Each code can only be used once.'}
          </p>
          <div className="two-factor-settings__backup-codes-list">
            {backupCodes.map((code, index) => (
              <code key={index} className="two-factor-settings__backup-code" data-testid={`backup-code[${index}]`} >
                {code}
              </code>
            ))}
          </div>
          <button
            type="button"
            className="two-factor-settings__button two-factor-settings__button--primary"
            onClick={handleBackupCodesDismiss}
          >
            {S.done || 'Done'}
          </button>
        </div>
      </div>
    );
  };

  const renderMethodSelector = () => {
    return (
      <div className="two-factor-settings__setup">
        <div className="two-factor-settings__setup-container">
          <h3>{S.twoFactorSettings?.setupTitle || 'Set Up Two-Factor Authentication'}</h3>
          <p>{S.twoFactorSettings?.chooseMethod || 'Choose how you want to receive verification codes:'}</p>

          {error && (
            <div className="two-factor-settings__error">
              {error}
            </div>
          )}

          <div className="two-factor-settings__method-options">
            <button
              type="button"
              className="two-factor-settings__method-option"
              onClick={() => handleMethodSelect('totp')}
              disabled={actionLoading}
            >
              <div className="two-factor-settings__method-icon two-factor-settings__method-icon--totp">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12" y2="18" />
                </svg>
              </div>
              <div className="two-factor-settings__method-text">
                <span className="two-factor-settings__method-title">
                  {S.twoFactorSettings?.methodTotp || 'Authenticator App'}
                </span>
                <span className="two-factor-settings__method-description">
                  {S.twoFactorSettings?.methodTotpDesc || 'Use an app like Google Authenticator or Authy'}
                </span>
              </div>
            </button>

            <button
              type="button"
              className="two-factor-settings__method-option"
              onClick={() => handleMethodSelect('sms')}
              disabled={actionLoading}
            >
              <div className="two-factor-settings__method-icon two-factor-settings__method-icon--sms">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className="two-factor-settings__method-text">
                <span className="two-factor-settings__method-title">
                  {S.twoFactorSettings?.methodSms || 'SMS'}
                </span>
                <span className="two-factor-settings__method-description">
                  {S.twoFactorSettings?.methodSmsDesc || 'Receive codes via text message'}
                </span>
              </div>
            </button>

            <button
              type="button"
              className="two-factor-settings__method-option"
              onClick={() => handleMethodSelect('email')}
              disabled={actionLoading}
            >
              <div className="two-factor-settings__method-icon two-factor-settings__method-icon--email">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div className="two-factor-settings__method-text">
                <span className="two-factor-settings__method-title">
                  {S.twoFactorSettings?.methodEmail || 'Email'}
                </span>
                <span className="two-factor-settings__method-description">
                  {S.twoFactorSettings?.methodEmailDesc || 'Receive codes via email'}
                </span>
              </div>
            </button>
          </div>

          {actionLoading && (
            <div className="two-factor-settings__loading">
              {S.loading || 'Loading...'}
            </div>
          )}

          <div className="two-factor-settings__button-group">
            <button
              type="button"
              className="two-factor-settings__button two-factor-settings__button--secondary"
              onClick={handleCancelEnable}
              disabled={actionLoading}
            >
              {S.cancel || 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEnableSetup = () => {
    if (!enableData) return null;

    const isTotp = selectedMethod === 'totp';
    const instructionText = isTotp
      ? (S.twoFactorSettings?.setupInstructions || 'Scan this QR code with your authenticator app:')
      : selectedMethod === 'sms'
        ? (S.twoFactorSettings?.smsInstructions || 'A verification code has been sent to your phone.')
        : (S.twoFactorSettings?.emailInstructions || 'A verification code has been sent to your email.');

    const codeLabel = isTotp
      ? (S.twoFactorSettings?.enterCode || 'Enter the 6-digit code from your app:')
      : (S.twoFactorSettings?.enterCodeSent || 'Enter the 6-digit code you received:');

    return (
      <div className="two-factor-settings__setup">
        <div className="two-factor-settings__setup-container">
          <h3>{S.twoFactorSettings?.setupTitle || 'Set Up Two-Factor Authentication'}</h3>
          <p>{instructionText}</p>

          {isTotp && enableData.qr_url && (
            <>
              <div className="two-factor-settings__qr-container">
                <div className="two-factor-settings__qr-code">
                  <QRCode value={enableData.qr_url} size={200} />
                </div>
              </div>
              <p className="two-factor-settings__manual-entry">
                {S.twoFactorSettings?.manualEntry || "Or enter this code manually:"}
                <code className="two-factor-settings__secret">{enableData.secret}</code>
              </p>
            </>
          )}

          <div className="two-factor-settings__verify-section">
            <label htmlFor="verify-code">{codeLabel}</label>
            <input
              id="verify-code"
              type="text"
              className="two-factor-settings__code-input"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
            />
          </div>
          <div className="two-factor-settings__button-group">
            <button
              type="button"
              className="two-factor-settings__button two-factor-settings__button--secondary"
              onClick={handleCancelEnable}
              disabled={actionLoading}
            >
              {S.cancel || 'Cancel'}
            </button>
            <button
              type="button"
              className={`two-factor-settings__button two-factor-settings__button--primary${
                verifyCode.length !== 6 ? ' two-factor-settings__button--incomplete' : ''
              }`}
              onClick={verifyCode.length === 6 ? handleEnableConfirm : undefined}
            >
              {actionLoading ? S.loading || 'Loading...' : S.confirm || 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPasswordConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel: () => void
  ) => {
    return (
      <div className="two-factor-settings__confirm-modal">
        <div className="two-factor-settings__confirm-content">
          <h3>{title}</h3>
          <p>{message}</p>
          <div className="two-factor-settings__password-section">
            <label htmlFor="confirm-password">
              {S.password || 'Password'}
            </label>
            <input
              id="confirm-password"
              type="password"
              className="two-factor-settings__password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={S.placeholder?.password || 'Password'}
            />
          </div>
          <div className="two-factor-settings__button-group">
            <button
              type="button"
              className="two-factor-settings__button two-factor-settings__button--secondary"
              onClick={onCancel}
              disabled={actionLoading}
            >
              {S.cancel || 'Cancel'}
            </button>
            <button
              type="button"
              className={`two-factor-settings__button two-factor-settings__button--primary${
                !password ? ' two-factor-settings__button--incomplete' : ''
              }`}
              onClick={password ? onConfirm : undefined}
              disabled={actionLoading}
            >
              {actionLoading ? S.loading || 'Loading...' : S.confirm || 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStatus = () => {
    if (!status) return null;

    if (enableStep === 'select-method') {
      return renderMethodSelector();
    }

    if (enableStep === 'setup' || enableStep === 'verify') {
      return renderEnableSetup();
    }

    return (
      <>
        {/* Password Section */}
        <div className="two-factor-settings__status-section two-factor-settings__password-section">
          <div className="two-factor-settings__status-row">
            <span className="two-factor-settings__label">
              {S.twoFactorSettings?.password || 'Password'}
            </span>
            <span className="two-factor-settings__value">••••••••</span>
            <button
              type="button"
              className="two-factor-settings__link-button"
              onClick={() => setShowPasswordModal(true)}
            >
              {S.twoFactorSettings?.changePassword || 'Change'}
            </button>
          </div>
        </div>

        {/* 2FA Section */}
        <div className="two-factor-settings__status-section">
          <div className="two-factor-settings__status-row">
            <span className="two-factor-settings__label">
              {S.twoFactorSettings?.status || 'Two-Factor Authentication'}
            </span>
            <span className="two-factor-settings__value"></span>
            <span
              className={`two-factor-settings__status-badge ${
                status.enabled
                  ? 'two-factor-settings__status-badge--enabled'
                  : 'two-factor-settings__status-badge--disabled'
              }`}
              data-testid={`is-2fa-enabled`}
            >
              {status.enabled
                ? S.enabled || 'Enabled'
                : S.disabled || 'Disabled'}
            </span>
          </div>

        {status.enabled ? (
          <>
            {status.backup_codes_count !== undefined && (
              <div className="two-factor-settings__status-row">
                <span className="two-factor-settings__label">
                  {S.twoFactorSettings?.backupCodesRemaining || 'Backup Codes Remaining'}
                </span>
                <span className="two-factor-settings__value" data-testid={`backup-codes-count`}>{status.backup_codes_count}</span>
                <button
                  type="button"
                  className="two-factor-settings__link-button"
                  onClick={() => {
                    setShowRegenerateConfirm(true);
                    setPassword('');
                    setError(null);
                  }}
                >
                  {S.twoFactorSettings?.regenerateCodes || 'Regenerate'}
                </button>
              </div>
            )}
            <div className="two-factor-settings__actions">
              <button
                type="button"
                className="two-factor-settings__button two-factor-settings__button--primary"
                onClick={() => {
                  setShowDisableConfirm(true);
                  setPassword('');
                  setError(null);
                }}
              >
                {S.twoFactorSettings?.disable || 'Disable 2FA'}
              </button>
            </div>
          </>
        ) : (
          <div className="two-factor-settings__actions">
            <button
              type="button"
              className="two-factor-settings__button two-factor-settings__button--primary"
              onClick={handleEnableStart}
              disabled={actionLoading}
            >
              {actionLoading
                ? S.loading || 'Loading...'
                : S.twoFactorSettings?.enable || 'Enable 2FA'}
            </button>
          </div>
        )}
        </div>
      </>
    );
  };

  return (
    <div className="two-factor-settings">
      <PageHeader left={S.twoFactorSettings?.title || 'Security'} />
      <WithLoader isLoading={isLoading}>
        <div className="two-factor-settings__content">
          {error && (
            <div className="two-factor-settings__error">
              {error}
            </div>
          )}
          {renderStatus()}
        </div>
      </WithLoader>

      {showDisableConfirm &&
        renderPasswordConfirm(
          S.twoFactorSettings?.disableTitle || 'Disable Two-Factor Authentication',
          S.twoFactorSettings?.disableWarning ||
            'Enter your password to disable two-factor authentication. This will make your account less secure.',
          handleDisable,
          () => {
            setShowDisableConfirm(false);
            setPassword('');
            setError(null);
          }
        )}

      {showRegenerateConfirm &&
        renderPasswordConfirm(
          S.twoFactorSettings?.regenerateTitle || 'Regenerate Backup Codes',
          S.twoFactorSettings?.regenerateWarning ||
            'Enter your password to regenerate backup codes. Your old codes will no longer work.',
          handleRegenerate,
          () => {
            setShowRegenerateConfirm(false);
            setPassword('');
            setError(null);
          }
        )}

      {renderBackupCodes()}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="two-factor-settings__confirm-modal">
          <div className="two-factor-settings__confirm-content">
            <h3>{S.twoFactorSettings?.changePasswordTitle || 'Change Password'}</h3>

            {passwordSuccess ? (
              <div className="two-factor-settings__success">
                {S.twoFactorSettings?.passwordUpdated || 'Password updated successfully'}
              </div>
            ) : (
              <>
                {passwordError && (
                  <div className="two-factor-settings__error">
                    {passwordError}
                  </div>
                )}

                <div className="two-factor-settings__password-section">
                  <label htmlFor="current-password">
                    {S.twoFactorSettings?.currentPassword || 'Current Password'}
                  </label>
                  <PasswordInput
                    id="current-password"
                    value={currentPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentPassword(e.target.value)}
                    placeholder={S.placeholder?.password || 'Current password'}
                    align="left"
                  />
                </div>

                <div className="two-factor-settings__password-section">
                  <label htmlFor="new-password">
                    {S.twoFactorSettings?.newPassword || 'New Password'}
                  </label>
                  <PasswordInput
                    id="new-password"
                    value={newPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                    placeholder={S.twoFactorSettings?.newPasswordPlaceholder || 'New password'}
                    align="left"
                  />
                  <ul className="two-factor-settings__password-requirements">
                    {(() => {
                      const reqs = checkPasswordRequirements(newPassword);
                      return (
                        <>
                          <li className={reqs.minLength ? 'met' : ''}>
                            <span className="indicator">{reqs.minLength ? '✓' : '○'}</span>
                            At least 8 characters
                          </li>
                          <li className={reqs.hasUppercase ? 'met' : ''}>
                            <span className="indicator">{reqs.hasUppercase ? '✓' : '○'}</span>
                            One uppercase letter
                          </li>
                          <li className={reqs.hasLowercase ? 'met' : ''}>
                            <span className="indicator">{reqs.hasLowercase ? '✓' : '○'}</span>
                            One lowercase letter
                          </li>
                          <li className={reqs.hasNumber ? 'met' : ''}>
                            <span className="indicator">{reqs.hasNumber ? '✓' : '○'}</span>
                            One number
                          </li>
                          <li className={reqs.hasSpecial ? 'met' : ''}>
                            <span className="indicator">{reqs.hasSpecial ? '✓' : '○'}</span>
                            One special character
                          </li>
                          <li className={newPassword && newPassword !== currentPassword ? 'met' : ''}>
                            <span className="indicator">{newPassword !== currentPassword ? '✓' : '○'}</span>
                            New password must be different from current password
                          </li>
                        </>
                      );
                    })()}
                  </ul>
                </div>

                <div className="two-factor-settings__password-section">
                  <label htmlFor="confirm-password">
                    {S.twoFactorSettings?.confirmPassword || 'Confirm New Password'}
                  </label>
                  <PasswordInput
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                    placeholder={S.twoFactorSettings?.confirmPasswordPlaceholder || 'Confirm new password'}
                    align="left"
                  />
                </div>

                <div className="two-factor-settings__button-group">
                  <button
                    type="button"
                    className="two-factor-settings__button two-factor-settings__button--secondary"
                    onClick={handlePasswordModalClose}
                    disabled={passwordLoading}
                  >
                    {S.cancel || 'Cancel'}
                  </button>
                  <button
                    type="button"
                    className={`two-factor-settings__button two-factor-settings__button--primary${
                      !isPasswordValid(checkPasswordRequirements(newPassword)) || newPassword !== confirmPassword || newPassword === currentPassword
                        ? ' two-factor-settings__button--incomplete'
                        : ''
                    }`}
                    disabled={
                      !isPasswordValid(checkPasswordRequirements(newPassword)) || newPassword !== confirmPassword || newPassword === currentPassword || passwordLoading
                    }
                    onClick={handlePasswordChange}
                  >
                    {passwordLoading ? S.loading || 'Loading...' : S.twoFactorSettings?.updatePassword || 'Update Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  get2FAStatus: () => dispatch(actions.get2FAStatus()),
  disable2FA: (password: string) => dispatch(actions.disable2FA(password)),
  regenerate2FACodes: (password: string) => dispatch(actions.regenerate2FACodes(password)),
  enable2FAStart: (method: TwoFactorMethod) => dispatch(actions.enable2FAStart(method)),
  enable2FAConfirm: (code: string) => dispatch(actions.enable2FAConfirm(code)),
  updatePassword: (currentPassword: string, newPassword: string) => dispatch(actions.updatePassword(currentPassword, newPassword)),
  refreshProfile: () => dispatch(userProfile()),
});

export default connect(null, mapDispatchToProps)(TwoFactorSettings);
